import {
  funnelGeometry,
  funnelViewBoxHeight,
  hasFunnelData,
  stageConversionRatio,
  totalConversionRatio,
  totalConversionRatioPrev,
  FUNNEL_WIDTH,
  type FunnelStage,
} from "@/features/kpi/funnel";
import { t } from "@/i18n";
import { fmtInt, fmtPct } from "@/lib/utils";

const RAMP = ["#1A56DB", "#2563EB", "#3B82F6", "#60A5FA", "#93C5FD", "#BFDBFE"];

interface FunnelSvgProps {
  stages: FunnelStage[];
  /** GA4 "kapali huni" mi (her asama onceki asamalari sirayla tamamlar) — dipnot metnini degistirir. */
  closed?: boolean;
}

/** E-ticaret satis hunisi — kpi.js `renderEcomFunnel` portu (bkz. features/kpi/funnel.ts). */
export function FunnelSvg({ stages, closed = false }: FunnelSvgProps) {
  if (!hasFunnelData(stages)) {
    return <EmptyFunnel />;
  }

  const geometry = funnelGeometry(stages);
  const viewBoxHeight = funnelViewBoxHeight(stages.length);
  const first = stages[0];
  const last = stages[stages.length - 1];
  const totalRatio = totalConversionRatio(stages);
  const totalPrevRatio = totalConversionRatioPrev(stages);

  return (
    <div className="space-y-3" data-testid="funnel-svg">
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
        <div className="space-y-1">
          {stages.map((s, i) => (
            <div key={s.label} className="text-right text-xs text-muted">
              <span className="font-medium text-ink">
                {i + 1}. {s.label}
              </span>
              <div className="text-sm font-semibold text-ink">{fmtInt(s.count)}</div>
            </div>
          ))}
        </div>

        <svg
          width={FUNNEL_WIDTH / 2}
          viewBox={`0 0 ${FUNNEL_WIDTH} ${viewBoxHeight}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={t("funnel.aria_label")}
        >
          {geometry.map((g) => (
            <path key={g.index} fill={RAMP[g.index % RAMP.length]} d={g.path}>
              <title>
                {g.label}: {fmtInt(g.count)}
              </title>
            </path>
          ))}
        </svg>

        <div className="space-y-1">
          {stages.map((s, i) => {
            const ratio = stageConversionRatio(stages, i);
            return (
              <div key={s.label} className="text-xs text-muted">
                {i === 0 ? (
                  <span className="font-semibold text-ink">100%</span>
                ) : (
                  <span className="font-semibold text-ink">{ratio != null ? fmtPct(ratio) : "—"}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <p className="text-xs text-muted">
        {first.label} → {last.label}: <strong className="text-ink">{totalRatio != null ? fmtPct(totalRatio) : "—"}</strong>
        {totalPrevRatio != null && ` (${t("funnel.previous_period")} ${fmtPct(totalPrevRatio)})`}
        {" · "}
        {closed ? t("funnel.note_closed") : t("funnel.note_open")}
      </p>
    </div>
  );
}

function EmptyFunnel() {
  return (
    <div role="status" className="rounded-lg border border-dashed border-line bg-card p-4 text-center text-sm text-muted">
      {t("empty.not_connected.title")}
      <p className="mt-1 text-xs">{t("funnel.empty_body")}</p>
    </div>
  );
}
